// ============================================================================
// FSA — MULTI-COLUMN NOTE: CLASS ROLLUP FROM LINE ITEMS (Option A)
// ============================================================================
// On a multi-column Note a class that carries line items is a read-only
// rollup: class.column_values[col] = Σ line_items[].column_values[col], per
// amount column, with the __rounded companion summed alongside. The PDF's
// per-section Total (Owners' Capital) and the class row read that dict
// verbatim, so it MUST be persisted fresh.
//
// The bug this closes (Jososns Hardware, Partners' Current Account): the
// saved class rollup was stale against its own line items. On open the
// hydrate merge (applyAppropriationRouting) re-rolled it in memory BEFORE
// the baseline snapshot, so the screen showed the right Total and the note
// was not dirty. canSave only compares the legacy amount_actual, which the
// backend already re-rolls from line items — so Save never lit and the PDF
// kept printing the stale Total.
//
// The fix: normalizeWorkspaceForSave re-rolls every such class on every
// save (then recomputes formula columns as before), and canSave offers Save
// whenever that would change a saved class dict (hasClassRollupDrift).
// Client-only by design — the backend trusts the client's column_values;
// a server-side Σ would fight the client's formula recompute of computed
// __rounded columns and keep Save lit forever.
//
// Rules:
//   • amount columns only.
//   • a key is rolled only when at least one line item carries it as a
//     number; other class keys are left untouched (a legacy line item with
//     no column_values must not zero a class value).
//   • classes with a template subtotal_formula are skipped — their class
//     value is not a plain Σ.

import { ROUNDED_SUFFIX } from './rounding';

/** Minimal column shape — satisfied by FormatColumnSpec. */
export interface RollupColumn {
  code: string;
  data_type?: string;
}

/** Minimal line-item shape — satisfied by NoteLineItem. */
export interface RollupLineItem {
  column_values?: Record<string, unknown> | null;
}

/** Minimal class shape — satisfied by NoteClassData. */
export interface RollupClass {
  column_values?: Record<string, unknown> | null;
  line_items?: ReadonlyArray<RollupLineItem> | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Every amount key the rollup owns: `<code>` and `<code>__rounded`. */
function amountKeys(columns: ReadonlyArray<RollupColumn>): string[] {
  const keys: string[] = [];
  for (const col of columns) {
    if (col.data_type !== 'amount') continue;
    keys.push(col.code, col.code + ROUNDED_SUFFIX);
  }
  return keys;
}

/**
 * Σ line items per amount key. Only keys carried by at least one line item
 * appear in the result. Empty when the class has no line items.
 */
export function sumLineItemColumnValues(
  lineItems: ReadonlyArray<RollupLineItem> | null | undefined,
  columns: ReadonlyArray<RollupColumn>,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!lineItems || lineItems.length === 0) return out;
  for (const key of amountKeys(columns)) {
    let carried = false;
    let sum = 0;
    for (const li of lineItems) {
      const v = li.column_values?.[key];
      if (typeof v === 'number') { carried = true; sum += v; }
    }
    if (carried) out[key] = round2(sum);
  }
  return out;
}

/**
 * Returns the class column_values with every line-item-carried amount key
 * replaced by Σ line items. Returns the input unchanged (same reference)
 * when the class has no line items or nothing differs.
 */
export function rollupClassColumnValues<T extends Record<string, unknown>>(
  classCv: T | null | undefined,
  lineItems: ReadonlyArray<RollupLineItem> | null | undefined,
  columns: ReadonlyArray<RollupColumn>,
): T | null | undefined {
  const sums = sumLineItemColumnValues(lineItems, columns);
  const keys = Object.keys(sums);
  if (keys.length === 0) return classCv;
  if (classCv && keys.every(k => classCv[k] === sums[k])) return classCv;
  return { ...(classCv ?? {}), ...sums } as unknown as T;
}

/**
 * True when Save would change a SAVED class rollup — i.e. what the PDF
 * prints. `normalizedClassData` is the save normaliser's output for the
 * current workspace (rollup + formula recompute), so right after a save the
 * two are identical and this settles. Only classes carrying line items are
 * compared, over the amount keys the normalised class holds; a key absent
 * from the saved dict reads as 0.
 */
export function hasClassRollupDrift(
  savedClassData: Record<string, RollupClass> | null | undefined,
  normalizedClassData: Record<string, RollupClass> | null | undefined,
  columns: ReadonlyArray<RollupColumn> | null | undefined,
): boolean {
  if (!normalizedClassData || !columns || columns.length === 0) return false;
  const keys = amountKeys(columns);
  for (const [code, cd] of Object.entries(normalizedClassData)) {
    if (!cd?.line_items || cd.line_items.length === 0) continue;
    const nextCv = cd.column_values ?? {};
    const savedCv = savedClassData?.[code]?.column_values ?? {};
    for (const key of keys) {
      const next = nextCv[key];
      if (typeof next !== 'number') continue;
      const saved = savedCv[key];
      const savedNum = typeof saved === 'number' ? saved : 0;
      if (Math.abs(savedNum - next) > 0.005) return true;
    }
  }
  return false;
}
